/**
 * The Hatke Shopping Assistant — storefront widget v1.6
 * Self-contained: inject one <script> tag, no dependencies.
 *
* v1.4 (16 Sep 2026):
 *   - MOBILE KEYBOARD FIX. The panel used 100dvh. dvh is the DYNAMIC viewport
 *     height: opening the keyboard hides the browser URL bar, which makes dvh
 *     LARGER — and when the keyboard closes the URL bar stays hidden, so the
 *     panel kept the bigger height and never shrank back.
 *     Now the CSS base is 100svh (the SMALL viewport height, which never
 *     changes), and while the panel is open we size it to
 *     window.visualViewport — the rect that is actually visible. That both
 *     stops the growth and keeps the input above the keyboard instead of
 *     behind it.
 * v1.3 (4 Sep 2026):
 *   - Cart-drawer nudge fires IMMEDIATELY on drawer close (overlay path). The
 *     quiet-period countdown is kept only for the click path, where we cannot
 *     see the drawer and must infer the customer is back on the page.
 *   - RESTORED 7-day expiry on once-per-visitor flags (hb_modal_shown,
 *     hb_exit_shown). v1.2 read them as plain localStorage, which made them
 *     permanent: anyone who saw the exit modal once never saw it again, and the
 *     addressable audience shrank every day. Legacy "1" values are treated as
 *     expired so existing visitors become eligible again.
 * v1.2 (4 Sep 2026): cart-close nudge fires only after N quiet seconds (default 10)
 * v1.1 (4 Sep 2026):
 *   - DEFERRED BOOT: widget initialises on first scroll/tap/keypress or at 3s,
 *     whichever comes first. Nothing runs during first paint.
 *   - NUDGE SLOTS: every modal/tooltip is gated by `nudge_slots` from
 *     /widget-config (server default = exit-intent only). Change slots in the
 *     bot admin config, no theme edit needed.
 *   - `nudges` accepted as list OR {rows:[...]} object.
 *
 * Install:
 *   In theme.liquid, just before </body>:
 *     <script src="{{ 'hatke-bot.js' | asset_url }}" defer></script>
 *   Upload this file as hatke-bot.js in the theme's Assets.
 */
(function () {
  "use strict";

  // ======== CONFIG ========
  var BACKEND = "https://bot.thehatke.com";
  var TEST_MODE = false;
  var BRAND = {
    accent: "#1a1a1a",
    accentText: "#ffffff",
    action: "#c96f43",
    bg: "#ffffff",
    userBubble: "#1a1a1a",
    botBubble: "#f6f4f0",
    font: "inherit",
    name: "Riya",
    tagline: "Online · The Hatke",
    avatar: "https://cdn.shopify.com/s/files/1/0055/3074/6951/files/Gemini_Generated_Image_3wysna3wysna3wys.png?v=1786216519&width=200&format=webp",
  };
  // Which nudges may fire when the server sends no `nudge_slots` key.
  // The server's own default is identical (exit only).
  var SLOT_DEFAULTS = {
    idle: false, dwell: false, repeat: false, cart_leave: false,
    cart_drawer: false, milestone: false, tooltips: false, exit: true,
    checkout_return: false,
  };
  // ========================

  if (TEST_MODE) {
    try {
      if (new URLSearchParams(location.search).get("hatkebot") === "test")
        localStorage.setItem("hb_test", "1");
      if (localStorage.getItem("hb_test") !== "1") return;
    } catch (e) { return; }
  }

  // ======== DEFERRED BOOT ========
  var __hbStarted = false;
  function __hbStart() { if (__hbStarted) return; __hbStarted = true; __hbMain(); }
  ["scroll", "touchstart", "mousemove", "keydown", "pointerdown"].forEach(function (ev) {
    window.addEventListener(ev, __hbStart, { passive: true, once: true });
  });
  setTimeout(__hbStart, 3000);

  function __hbMain() {

  var state = {
    sessionKey: null,
    open: false,
    config: null,
    devicePromptShown: false,
    sending: false,
    upsellShownForCart: false,
  };

  function slotOn(name) {
    var sl = (state.config && state.config.nudge_slots) || {};
    return sl[name] === undefined ? !!SLOT_DEFAULTS[name] : !!sl[name];
  }

  // ---------- utilities ----------
  function $(sel, root) { return (root || document).querySelector(sel); }
  function el(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function inr(n) { return "₹" + Math.round(n).toLocaleString("en-IN"); }
  function api(path, body) {
    return fetch(BACKEND + path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(function (r) {
      if (!r.ok) throw new Error("api " + r.status);
      return r.json();
    });
  }
  var TAG_RANK = { visitor: 0, assisted: 1, direct: 2 };
  function tagCart(mode) {
    if (!state.sessionKey) return;
    if ((TAG_RANK[state.cartTag] || -1) >= TAG_RANK[mode]) return;
    state.cartTag = mode;
    fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: {
        hatkebot_session: state.sessionKey,
        hatkebot_mode: mode,
        hatkebot_chat_id: state.chatId || "",
      } }),
    }).catch(function () {});
  }

  function pageContext() {
    var ctx = { path: location.pathname };
    if (location.pathname.indexOf("/products/") !== -1) {
      var og = $('meta[property="og:title"]');
      ctx.product_title = og ? og.getAttribute("content") : document.title;
    }
    return ctx;
  }

  // ---------- styles ----------
  var css = "" +
    ".hb-bubble{position:fixed;right:18px;bottom:18px;z-index:2147483000;width:58px;height:58px;border-radius:50%;background:" + BRAND.accent + ";color:" + BRAND.accentText + ";display:flex;align-items:center;justify-content:center;cursor:pointer;box-shadow:0 6px 24px rgba(0,0,0,.25);border:none;transition:transform .15s;touch-action:manipulation}" +
    ".hb-bubble:hover{transform:scale(1.06)}" +
    ".hb-bubble svg{width:28px;height:28px}" +
    ".hb-bubble .hb-bav{width:100%;height:100%;border-radius:50%;object-fit:cover;display:block}" +
    ".hb-online{position:absolute;bottom:2px;right:2px;width:14px;height:14px;border-radius:50%;background:#3ddc84;border:2.5px solid #fff;box-sizing:border-box}" +
    ".hb-head .hb-hav{width:38px;height:38px;border-radius:50%;object-fit:cover;flex:0 0 auto;border:2px solid #f0ede8}" +
    ".hb-badge{position:absolute;top:-2px;right:-2px;background:" + BRAND.action + ";color:#fff;font-size:11px;font-weight:700;min-width:18px;height:18px;border-radius:9px;display:flex;align-items:center;justify-content:center;padding:0 4px}" +
    ".hb-tip{position:fixed;right:86px;bottom:32px;z-index:2147483000;background:#fff;color:#1c1a17;padding:11px 15px;border-radius:14px;border-bottom-right-radius:3px;font-size:14px;max-width:240px;box-shadow:0 8px 28px rgba(30,25,20,.22);border:1px solid #efece7;cursor:pointer;font-family:" + BRAND.font + "}" +
    ".hb-tip .hb-x{margin-left:8px;color:#b0aa9e;font-weight:700;cursor:pointer}" +
    ".hb-panel{position:fixed;right:18px;bottom:88px;z-index:2147483001;width:376px;max-width:calc(100vw - 24px);height:600px;max-height:calc(100vh - 110px);background:" + BRAND.bg + ";border-radius:20px;box-shadow:0 16px 50px rgba(30,25,20,.18);display:flex;flex-direction:column;overflow:hidden;font-family:" + BRAND.font + "}" +
    ".hb-raised .hb-bubble{bottom:calc(92px + env(safe-area-inset-bottom,0px))!important}" +
    ".hb-raised .hb-tip{bottom:calc(106px + env(safe-area-inset-bottom,0px))!important}" +
    // svh, NOT dvh: dvh grows when the keyboard hides the URL bar and never
    // shrinks back. svh assumes browser UI is visible and is constant, so the
    // panel has a stable base; syncPanelViewport() then tracks the keyboard.
    "@media(max-width:480px){.hb-panel{right:0;bottom:0;width:100vw;max-width:100vw;height:100svh;max-height:100svh;border-radius:0;padding-bottom:env(safe-area-inset-bottom,0px)}.hb-compose{padding-bottom:calc(10px + env(safe-area-inset-bottom,0px))}.hb-bubble{bottom:calc(18px + env(safe-area-inset-bottom,0px))}}" +
    ".hb-compose button,.hb-menu button,.hb-chip,.hb-add,.hb-morebtn,.hb-checkoutbtn,.hb-arrow,.hb-burger{touch-action:manipulation;-webkit-tap-highlight-color:rgba(0,0,0,.1)}" +
    ".hb-head{background:#fff;color:#141414;padding:14px 16px;display:flex;align-items:center;gap:10px;flex:0 0 auto;border-bottom:1px solid #efece7}" +
    ".hb-head .hb-dot{width:9px;height:9px;border-radius:50%;background:#3ddc84;flex:0 0 auto}" +
    ".hb-head .hb-title{font-weight:700;font-size:15px;line-height:1.2}" +
    ".hb-head .hb-sub{font-size:12px;color:#9a9488}" +
    ".hb-close{margin-left:auto;background:none;border:none;color:#8a8577;font-size:22px;cursor:pointer;line-height:1;padding:4px}" +
    ".hb-msgs{flex:1 1 auto;overflow-y:auto;padding:14px;display:flex;flex-direction:column;gap:10px;scroll-behavior:smooth}" +
    ".hb-m{max-width:85%;padding:10px 14px;border-radius:16px;font-size:14px;line-height:1.45;white-space:pre-wrap;word-wrap:break-word}" +
    ".hb-m a{color:" + BRAND.action + ";font-weight:600}" +
    ".hb-m.user{align-self:flex-end;background:" + BRAND.userBubble + ";color:#fff;border-bottom-right-radius:4px}" +
    ".hb-m.bot{align-self:flex-start;background:" + BRAND.botBubble + ";color:#111;border-bottom-left-radius:4px}" +
    ".hb-typing{align-self:flex-start;background:" + BRAND.botBubble + ";border-radius:14px;padding:12px 16px;display:flex;gap:4px}" +
    ".hb-typing i{width:7px;height:7px;border-radius:50%;background:#999;animation:hbBlink 1.2s infinite}" +
    ".hb-typing i:nth-child(2){animation-delay:.2s}.hb-typing i:nth-child(3){animation-delay:.4s}" +
    "@keyframes hbBlink{0%,80%,100%{opacity:.25}40%{opacity:1}}" +
    ".hb-cards{display:flex;gap:10px;overflow-x:auto;padding:4px 2px 8px;scroll-snap-type:x mandatory;-webkit-overflow-scrolling:touch;min-height:265px}" +
    ".hb-card{flex:0 0 180px!important;min-width:180px!important;max-width:180px!important;scroll-snap-align:start;border:1px solid #eeeae4!important;border-radius:12px;overflow:hidden;background:#fff!important;display:flex!important;flex-direction:column!important;min-height:250px;box-sizing:border-box}" +
    ".hb-card .hb-cimg{display:block;width:100%!important;height:150px!important;background:#f5f5f5 center/cover no-repeat}" +
    ".hb-card img{width:100%!important;height:150px!important;max-width:none!important;object-fit:cover;display:block!important;background:#fafafa;margin:0!important}" +
    ".hb-card .hb-ct{font-size:12.5px;font-weight:600;color:#111!important;padding:8px 10px 2px;line-height:1.35;text-decoration:none;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;height:59px}" +
    ".hb-cards-wrap{position:relative}" +
    ".hb-arrow{position:absolute;right:2px;top:110px;width:34px;height:34px;border-radius:50%;background:" + BRAND.accent + ";color:#fff;border:none;font-size:20px;font-weight:700;cursor:pointer;box-shadow:0 3px 10px rgba(0,0,0,.3);z-index:2;display:flex;align-items:center;justify-content:center;line-height:1}" +
    ".hb-arrow-l{right:auto;left:2px}" +
    ".hb-more{display:flex!important;align-items:center;justify-content:center;background:#faf7f2!important;border:1.5px dashed " + BRAND.action + "!important}" +
    ".hb-morebtn{background:none;border:none;color:" + BRAND.action + ";font-weight:700;font-size:14px;cursor:pointer;line-height:1.5;padding:14px;text-align:center}" +
    ".hb-checkout-wrap{align-self:flex-start;margin:2px 0}" +
    ".hb-checkoutbtn{background:" + BRAND.accent + ";color:#fff;border:none;border-radius:22px;padding:11px 20px;font-size:14px;font-weight:700;cursor:pointer;box-shadow:0 3px 12px rgba(20,20,20,.28)}" +
    ".hb-burger{background:#fff;border:1.5px solid #e6e1d9;border-radius:50%;width:42px;height:42px;cursor:pointer;display:flex;align-items:center;justify-content:center;flex:0 0 auto;font-size:18px;color:" + BRAND.accent + "}" +
    ".hb-menu{position:absolute;left:10px;right:10px;bottom:64px;background:#fff;border:1px solid #e5e5e5;border-radius:14px;box-shadow:0 -6px 30px rgba(0,0,0,.18);z-index:3;overflow:hidden}" +
    ".hb-menu button{all:unset;display:block;width:100%;box-sizing:border-box;padding:13px 16px;font-size:14px;font-weight:600;color:#141414;cursor:pointer;border-bottom:1px solid #f2f2f2}" +
    ".hb-menu button:last-child{border-bottom:none}" +
    ".hb-menu button:hover{background:#fdeee4}" +
    ".hb-overlay{position:fixed!important;top:0!important;left:0!important;right:0!important;bottom:0!important;width:100vw;height:100vh;background:rgba(0,0,0,.55);z-index:2147483002;display:flex;align-items:center;justify-content:center;padding:20px;box-sizing:border-box}" +
    ".hb-modal{background:#fff;border-radius:16px;max-width:400px;width:100%;margin:auto;padding:30px 26px;text-align:center;position:relative;box-shadow:0 20px 60px rgba(0,0,0,.35);font-family:" + BRAND.font + "}" +
    ".hb-modal .hb-mx{position:absolute;top:10px;right:14px;background:none;border:none;font-size:22px;color:#999;cursor:pointer;line-height:1}" +
    ".hb-modal h3{font-size:22px;font-weight:800;color:#141414;margin:0 0 12px}" +
    ".hb-modal p{font-size:15px;color:#444;line-height:1.5;margin:0 0 20px}" +
    ".hb-modal .hb-mcta{background:" + BRAND.action + ";color:#fff;border:none;border-radius:10px;padding:13px 26px;font-size:15px;font-weight:700;cursor:pointer;width:100%}" +
    ".hb-card .hb-cp{padding:2px 10px 8px;font-size:14px;font-weight:700;color:" + BRAND.action + "}" +
    ".hb-card .hb-cp s{color:#999;font-weight:400;font-size:12px;margin-left:6px}" +
    ".hb-card .hb-cbtns{display:flex;gap:6px;padding:0 10px 6px;margin-top:auto}" +
    ".hb-card .hb-similar{background:none;border:none;color:" + BRAND.action + ";font-size:12px;font-weight:600;cursor:pointer;padding:0 10px 10px;text-align:left;text-decoration:underline}" +
    ".hb-card .hb-add{flex:1;background:" + BRAND.accent + ";color:#fff;border:none;border-radius:8px;padding:8px 0;font-size:13px;font-weight:700;cursor:pointer}" +
    ".hb-card .hb-view{flex:1;background:#fff;color:#111;border:1.5px solid #e6e1d9;border-radius:8px;padding:8px 0;font-size:13px;font-weight:600;text-align:center;text-decoration:none}" +
    ".hb-chips{display:flex;flex-wrap:wrap;gap:8px;padding:4px 0}" +
    ".hb-chip{background:#fff;border:1.5px solid #d8d2c8;color:#2a2620;border-radius:18px;padding:7px 14px;font-size:13px;font-weight:600;cursor:pointer}" +
    ".hb-chip:hover{border-color:" + BRAND.action + ";color:" + BRAND.action + "}" +
    ".hb-compose{flex:0 0 auto;display:flex;gap:8px;padding:10px;border-top:1px solid #eee;background:#fff}" +
    // 16px is MANDATORY, not a style choice. iOS Safari auto-zooms the whole
    // page when a focused input has font-size < 16px, and it does NOT zoom
    // back out on blur — the page stays zoomed, the chat panel overflows the
    // screen on both sides and its header is cut off. That is the "window got
    // bigger and never came back" bug. Measured 16 Sep 2026 on a real iPhone.
    // Do not lower this below 16px.
    ".hb-input{flex:1;border:1.5px solid #e6e1d9;border-radius:22px;padding:10px 16px;font-size:16px;outline:none;font-family:inherit}" +
    ".hb-input:focus{border-color:" + BRAND.accent + "}" +
    ".hb-send{background:" + BRAND.accent + ";color:#fff;border:none;border-radius:50%;width:42px;height:42px;cursor:pointer;display:flex;align-items:center;justify-content:center;flex:0 0 auto}" +
    ".hb-send:disabled{opacity:.4;cursor:default}" +
    ".hb-jump{position:absolute;bottom:76px;left:50%;transform:translateX(-50%);background:" + BRAND.accent + ";color:#fff;font-size:12px;padding:6px 12px;border-radius:14px;cursor:pointer;box-shadow:0 4px 12px rgba(0,0,0,.25);display:none;z-index:2}";
  var style = el("style"); style.textContent = css; document.head.appendChild(style);

  // ---------- DOM ----------
  var bubble = el("button", "hb-bubble");
  bubble.setAttribute("aria-label", "Chat with " + BRAND.name);
  bubble.innerHTML =
    (BRAND.avatar
      ? '<img class="hb-bav" src="' + BRAND.avatar + '" alt="' + esc(BRAND.name) + '"/>'
      : '<svg viewBox="0 0 24 24" fill="none"><path d="M12 3C7 3 3 6.6 3 11c0 2.2 1 4.1 2.6 5.5L5 21l4.1-1.8c.9.2 1.9.3 2.9.3 5 0 9-3.6 9-8S17 3 12 3z" fill="currentColor"/></svg>')
    + '<span class="hb-online"></span>';
  var badge = null;

  var panel = el("div", "hb-panel");
  panel.style.display = "none";
  panel.innerHTML =
    '<div class="hb-head">' +
    (BRAND.avatar ? '<img class="hb-hav" src="' + BRAND.avatar + '" alt=""/>' : '') +
    '<span class="hb-dot"></span><div><div class="hb-title">' + esc(BRAND.name) + '</div><div class="hb-sub">' + esc(BRAND.tagline) + '</div></div><button class="hb-close" aria-label="Close">×</button></div>' +
    '<div class="hb-msgs"></div>' +
    '<div class="hb-jump">↓ New messages</div>' +
    '<div class="hb-compose"><button class="hb-burger" aria-label="Menu">☰</button><input class="hb-input" placeholder="Type a message…" maxlength="1500"/><button class="hb-send" aria-label="Send"><svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M3 11l18-8-8 18-2-8-8-2z" fill="currentColor"/></svg></button></div>';

  document.body.appendChild(bubble);
  document.body.appendChild(panel);

  var msgs = $(".hb-msgs", panel);
  var input = $(".hb-input", panel);
  var sendBtn = $(".hb-send", panel);
  var jump = $(".hb-jump", panel);
  var burger = $(".hb-burger", panel);

  // ---------- burger menu ----------
  var menuEl = null;
  var DEFAULT_MENU = ["Track my order", "Find cases for my device", "Find accessories",
                      "Offers & discounts", "Cancel my order", "Contact customer support"];
  function startNewChat() {
    try { localStorage.removeItem("hb_session"); } catch (e) {}
    state.sessionKey = null;
    state.devicePromptShown = false;
    state.upsellShownForCart = false;
    state.milestoneEls = [];
    state.history = null;
    msgs.innerHTML = "";
    detectDevice().then(function (model) {
      return api("/session", { device_raw: model, path: location.pathname });
    })
      .then(function (s) {
        state.sessionKey = s.session_key;
        state.chatId = s.chat_id || "";
        state.devicePrompt = s.device_prompt || null;
        try { localStorage.setItem("hb_session", s.session_key); } catch (e) {}
        firstOpen();
      })
      .catch(function () { addMsg("bot", "Couldn't start a new chat — please refresh the page."); });
  }

  document.documentElement.classList.add("hb-raised");

  (function baselineTag() {
    var done = false;
    try { done = !!sessionStorage.getItem("hb_vtag"); } catch (e) {}
    if (done) { state.cartTag = state.cartTag || "visitor"; return; }
    var t = setInterval(function () {
      if (!state.sessionKey) return;
      clearInterval(t);
      tagCart("visitor");
      try { sessionStorage.setItem("hb_vtag", "1"); } catch (e) {}
    }, 800);
    setTimeout(function () { clearInterval(t); }, 20000);
  })();

  window.addEventListener("pageshow", function (e) {
    if (!e.persisted) return;
    var stored = null;
    try { stored = localStorage.getItem("hb_session"); } catch (err) {}
    if (stored && state.sessionKey && stored !== state.sessionKey) {
      state.sessionKey = stored;
      state.history = null;
      msgs.innerHTML = "";
      if (state.open) firstOpen();
    }
  });

  function closeMenu() { if (menuEl) { menuEl.remove(); menuEl = null; } }
  function toggleMenu() {
    if (menuEl) { closeMenu(); return; }
    var items = (state.config && state.config.menu && state.config.menu.items) || DEFAULT_MENU;
    if (location.pathname.indexOf("/products/") !== -1) {
      var pw2 = (state.config && state.config.product_welcome) || {};
      var pq = (pw2.chips && pw2.chips.length) ? pw2.chips
        : ["What's the material like?", "When will I receive it?", "Show me similar designs"];
      items = pq.concat(items);
    }
    menuEl = el("div", "hb-menu");
    items.forEach(function (label) {
      var b = el("button", null, esc(label));
      b.addEventListener("click", function () {
        closeMenu();
        if (isAccessoriesLabel(label)) accessoriesMenu(); else send(label);
      });
      menuEl.appendChild(b);
    });
    var nb = el("button", null, "🔄 Start a new chat");
    nb.addEventListener("click", function () { closeMenu(); startNewChat(); });
    menuEl.appendChild(nb);
    if (state.chatId) {
      var cid = el("div", null, "Chat ID: " + state.chatId + " · tap to copy");
      cid.style.cssText = "padding:8px 14px;font-size:11px;color:#9a9488;cursor:pointer;border-top:1px solid #efece7";
      cid.addEventListener("click", function () {
        try { navigator.clipboard.writeText(state.chatId); cid.textContent = "Copied ✓  " + state.chatId; } catch (e) {}
      });
      menuEl.appendChild(cid);
    }
    panel.appendChild(menuEl);
  }
  burger.addEventListener("click", function (e) { e.stopPropagation(); toggleMenu(); });
  panel.addEventListener("click", function (e) {
    if (menuEl && !menuEl.contains(e.target) && e.target !== burger) closeMenu();
  });
  input.addEventListener("focus", closeMenu);
  // iOS leaves the document scrolled after the keyboard closes; undo it.
  input.addEventListener("blur", function () { resyncSoon(); });
  input.addEventListener("focus", function () { resyncSoon(); });

  // ---------- rendering ----------
  function nearBottom() { return msgs.scrollHeight - msgs.scrollTop - msgs.clientHeight < 120; }
  function scrollBottom() { msgs.scrollTop = msgs.scrollHeight; jump.style.display = "none"; }
  function afterAppend(wasNear) {
    if (wasNear) scrollBottom(); else jump.style.display = "block";
  }
  msgs.addEventListener("scroll", function () { if (nearBottom()) jump.style.display = "none"; });
  jump.addEventListener("click", scrollBottom);

  function mdLite(t) {
    var s = esc(t);
    s = s.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>");
    s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
    s = s.replace(/(^|[^"'>])(https?:\/\/[^\s<]+[^\s<.,)!?'"])/g,
      '$1<a href="$2" target="_blank" rel="noopener" style="color:#c96f43;text-decoration:underline;word-break:break-all">$2</a>');
    return s;
  }

  function addMsg(role, text) {
    var wasNear = nearBottom();
    var m = el("div", "hb-m " + role, mdLite(text));
    msgs.appendChild(m);
    afterAppend(wasNear);
    return m;
  }

  function addTyping() {
    var t = el("div", "hb-typing", "<i></i><i></i><i></i>");
    msgs.appendChild(t); scrollBottom(); return t;
  }

  var CARDS_PER_PAGE = 10;

  function buildCard(p) {
    var card = el("div", "hb-card");
    card.innerHTML =
      '<a class="hb-cimg" href="' + esc(p.url) + '" target="_blank" rel="noopener">' +
      '<img loading="lazy" src="' + esc(p.image || "") + '" alt="' + esc(p.title) + '" onerror="this.style.display=\'none\'"/></a>' +
      '<a class="hb-ct" href="' + esc(p.url) + '" target="_blank" rel="noopener">' + esc(p.title) + '</a>' +
      '<div class="hb-cp">' + inr(p.price) + (p.compare_at ? "<s>" + inr(p.compare_at) + "</s>" : "") + '</div>' +
      '<div class="hb-cbtns"><button class="hb-add">ADD</button><a class="hb-view" href="' + esc(p.url) + '" target="_blank" rel="noopener">View</a></div>' +
      '<button class="hb-similar">Similar designs</button>';
    $(".hb-add", card).addEventListener("click", function () { addToCart(p, this); });
    $(".hb-similar", card).addEventListener("click", function () {
      send("Show me more designs similar to: " + p.title);
    });
    return card;
  }

  function addCards(products) {
    if (!products || !products.length) return;
    var wasNear = nearBottom();
    var outer = el("div", "hb-cards-wrap");
    var wrap = el("div", "hb-cards");
    var shown = 0;
    var moreBtn = null;

    function restick() { if (nearBottom()) scrollBottom(); }
    function watchImgs(root) {
      root.querySelectorAll("img").forEach(function (im) {
        im.addEventListener("load", restick);
        im.addEventListener("error", restick);
      });
    }

    function renderMore() {
      var batch = products.slice(shown, shown + CARDS_PER_PAGE);
      batch.forEach(function (p) {
        var card = buildCard(p);
        if (moreBtn) wrap.insertBefore(card, moreBtn); else wrap.appendChild(card);
        watchImgs(card);
      });
      shown += batch.length;
      var remaining = products.length - shown;
      if (remaining > 0 && !moreBtn) {
        moreBtn = el("div", "hb-card hb-more");
        wrap.appendChild(moreBtn);
      }
      if (moreBtn) {
        if (remaining > 0) {
          moreBtn.innerHTML = '<button class="hb-morebtn">View ' + remaining + ' more<br>designs →</button>';
          $(".hb-morebtn", moreBtn).addEventListener("click", function () {
            var keepX = wrap.scrollLeft;
            renderMore();
            wrap.scrollLeft = keepX;
          });
        } else {
          moreBtn.remove(); moreBtn = null;
        }
      }
    }

    renderMore();
    outer.appendChild(wrap);
    if (products.length > 1) {
      var arrowR = el("button", "hb-arrow", "›");
      arrowR.setAttribute("aria-label", "More products");
      arrowR.addEventListener("click", function () {
        wrap.scrollBy({ left: 200, behavior: "smooth" });
      });
      var arrowL = el("button", "hb-arrow hb-arrow-l", "‹");
      arrowL.setAttribute("aria-label", "Previous products");
      arrowL.style.display = "none";
      arrowL.addEventListener("click", function () {
        wrap.scrollBy({ left: -200, behavior: "smooth" });
      });
      wrap.addEventListener("scroll", function () {
        var atEnd = wrap.scrollWidth - wrap.scrollLeft - wrap.clientWidth < 30;
        arrowR.style.display = atEnd ? "none" : "flex";
        arrowL.style.display = wrap.scrollLeft > 30 ? "flex" : "none";
      });
      outer.appendChild(arrowR);
      outer.appendChild(arrowL);
    }
    msgs.appendChild(outer);
    afterAppend(wasNear);
    setTimeout(restick, 300);
    setTimeout(restick, 900);
  }

  function addChips(labels, onPick, sticky) {
    var wasNear = nearBottom();
    var wrap = el("div", "hb-chips");
    labels.forEach(function (l) {
      var c = el("button", "hb-chip", esc(l));
      c.addEventListener("click", function () {
        if (sticky) {
          c.remove();
          if (!wrap.children.length) wrap.remove();
        } else {
          wrap.remove();
        }
        onPick(l);
      });
      wrap.appendChild(c);
    });
    msgs.appendChild(wrap);
    afterAppend(wasNear);
  }

  // ---------- cart ----------
  function addToCart(p, btn) {
    state.lastChatAddTs = Date.now();
    var variant = (p.variants || []).filter(function (v) { return v.available; })[0];
    if (!variant) { addMsg("bot", "That one just went out of stock, sorry!"); return; }
    var vid = String(variant.id).split("/").pop();
    if (btn) { btn.textContent = "…"; btn.disabled = true; }
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: Number(vid), quantity: 1 }),
    })
      .then(function (r) { if (!r.ok) throw 0; return r.json(); })
      .then(function () {
        if (btn) btn.textContent = "ADDED ✓";
        tagCart("direct");
        return fetch("/cart.js").then(function (r) { return r.json(); });
      })
      .then(function (cart) { onCartChanged(cart, p); })
      .catch(function () {
        if (btn) { btn.textContent = "ADD"; btn.disabled = false; }
        addMsg("bot", "Couldn't add that just now — try from the product page: " + p.url);
      });
  }

  function onCartChanged(cart, addedProduct) {
    var total = cartGross(cart);
    var now = Date.now();
    if (state.lastCartEvent && now - state.lastCartEvent.t < 1500 &&
        state.lastCartEvent.total === total) return;
    state.lastCartEvent = { t: now, total: total };

    api("/event/cart", {
      session_key: state.sessionKey,
      cart_total: total,
      added_product_title: addedProduct ? addedProduct.title : null,
      added_product_type: addedProduct ? (addedProduct.product_type || null) : null,
      include_products: false,
    }).then(function (res) {
      if (state.milestoneEls) state.milestoneEls.forEach(function (e) { e.remove(); });
      state.milestoneEls = [];
      if (res.nudge) {
        var chatAdd = Date.now() - (state.lastChatAddTs || 0) < 20000;
        if (!chatAdd && !state.open) {
          // site-side cart activity with the chat closed: milestone modal
          // (gated by the "milestone" slot — off by default)
          if (!slotOn("milestone")) return;
          var seen = false;
          try { seen = !!sessionStorage.getItem("hb_milestone_modal"); } catch (e) {}
          if (!seen) {
            var shownNow = showModal({
              title: "Unlock more savings! 🎁",
              text: res.nudge,
              cta: "Show me the offers",
            }, openOffersFlow, true);
            if (shownNow) {
              try { sessionStorage.setItem("hb_milestone_modal", "1"); } catch (e) {}
            }
          }
          return;
        }
        openPanel();
        state.milestoneEls.push(addMsg("bot", res.nudge));
        var amt = res.cart_total != null ? " — ₹" + Math.round(res.cart_total) : "";
        var co = el("div", "hb-checkout-wrap",
          '<button class="hb-checkoutbtn">🛒 Go to Cart & Checkout' + amt + "</button>");
        $(".hb-checkoutbtn", co).addEventListener("click", goToCart);
        msgs.appendChild(co);
        state.milestoneEls.push(co);
        scrollBottom();
        if (!amt) fetch("/cart.js").then(function (r) { return r.json(); })
          .then(function (c) {
            if (c && c.total_price) {
              $(".hb-checkoutbtn", co).textContent =
                "🛒 Go to Cart & Checkout — ₹" + Math.round(c.total_price / 100);
            }
          }).catch(function () {});
      }
    }).catch(function (e) { console.warn("[hatke-bot] cart event failed:", e); });

    if (!state.upsellShownForCart) {
      state.upsellShownForCart = true;
      api("/event/cart", {
        session_key: state.sessionKey,
        cart_total: total,
        added_product_title: addedProduct ? addedProduct.title : null,
        added_product_type: addedProduct ? (addedProduct.product_type || null) : null,
        include_products: true,
      }).then(function (res) {
        if (res.products && res.products.length) {
          addMsg("bot", res.products_intro || "These go perfectly with it:");
          addCards(res.products);
        }
      }).catch(function (e) {
        state.upsellShownForCart = false;
        console.warn("[hatke-bot] upsell fetch failed:", e);
      });
    }
  }

  (function interceptCart() {
    var of = window.fetch.bind(window);
    window.fetch = function (url, opts) {
      var p = of.apply(null, arguments);
      try {
        var u = typeof url === "string" ? url : (url && url.url) || "";
        if (u.indexOf("/cart/add") !== -1) {
          p.then(function (r) {
            if (!r.ok) return;
            r.clone().json().then(function (added) {
              var item = (added && added.items && added.items[0]) || added || {};
              var pseudo = item.product_title
                ? { title: item.product_title, product_type: item.product_type || null }
                : null;
              setTimeout(function () {
                of("/cart.js").then(function (x) { return x.json(); })
                  .then(function (cart) { onCartChanged(cart, pseudo); })
                  .catch(function () {});
              }, 400);
            }).catch(function () {
              setTimeout(function () {
                of("/cart.js").then(function (x) { return x.json(); })
                  .then(function (cart) { onCartChanged(cart, null); })
                  .catch(function () {});
              }, 400);
            });
          }).catch(function () {});
        }
      } catch (e) {}
      return p;
    };
  })();

  // ---------- chat flow ----------
  function send(text) {
    if (!text || state.sending || !state.sessionKey) return;
    state.sending = true;
    input.value = ""; sendBtn.disabled = true;
    addMsg("user", text);
    tagCart("assisted");
    var typing = addTyping();
    var ctx = pageContext();
    fetch("/cart.js").then(function (r) { return r.json(); })
      .then(function (c) { return c ? cartGross(c) : null; })
      .catch(function () { return null; })
      .then(function (liveTotal) {
    api("/chat", {
      session_key: state.sessionKey,
      message: text,
      path: ctx.path,
      product_title: ctx.product_title || null,
      cart_total: liveTotal,
    })
      .then(function (res) {
        typing.remove();
        if (res.text) addMsg("bot", res.text);
        addCards(res.products);
        if (/🎁|offers:/i.test(res.text || "")) state.cartBtnAfterReply = true;
        if (location.pathname.indexOf("/products/") !== -1 &&
            (!res.products || !res.products.length) &&
            /cart|add it|checkout/i.test(res.text || "")) {
          addPageProductButton();
        }
      })
      .catch(function () {
        typing.remove();
        addMsg("bot", "Hmm, something went wrong on my side. Please try again in a moment 🙏");
      })
      .then(function () {
        state.sending = false; sendBtn.disabled = false;
        if (state.cartBtnAfterReply) {
          state.cartBtnAfterReply = false;
          appendCartButton();
        }
        if (!IS_MOBILE) input.focus();
      });
      });
  }

  sendBtn.addEventListener("click", function () { send(input.value.trim()); });
  input.addEventListener("keydown", function (e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input.value.trim()); }
  });

  var placeholders = ["Type a message…", "Ask me anything", "आप किसी भी भाषा में बात कर सकते हैं"];
  var phI = 0;
  setInterval(function () {
    if (document.activeElement !== input && !input.value) {
      phI = (phI + 1) % placeholders.length;
      input.placeholder = placeholders[phI];
    }
  }, 4000);

  // ---------- open/close, welcome, device prompt ----------
  var IS_MOBILE = window.matchMedia && window.matchMedia("(max-width: 480px)").matches;

  // MOBILE KEYBOARD / VIEWPORT SYNC
  // Neither dvh nor svh alone can follow the keyboard: dvh grows when the URL
  // bar hides and never shrinks back (the bug this fixes), while svh is stable
  // but lets the keyboard cover the input. visualViewport reports the rect that
  // is ACTUALLY visible, so while the panel is open we size to that and re-pin
  // on every change. Inline styles are cleared on close so the CSS takes over.
  // Safari animates the viewport for ~300ms after the keyboard moves and fires
  // no further event once it settles, so a single sync on the resize event can
  // land on a mid-animation value. Re-sync a couple of times afterwards.
  var IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
               (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  function resyncSoon() {
    setTimeout(syncPanelViewport, 60);
    setTimeout(syncPanelViewport, 350);
    setTimeout(syncPanelViewport, 700);
  }

  // MEASURED ON A REAL iPhone (16 Sep 2026), keyboard closed:
  //   innerH=660  vvH=660  vvTop=54  but the CSS 100svh panel was 714px tall.
  // So svh does NOT mean "the safe small viewport" here — it is the full
  // screen including the strip behind Safari's bottom toolbar, 54px more than
  // the customer can actually see. The panel overflowed by exactly that much
  // and its header sat off the top of the screen (panelTop=-54), which is what
  // "the window got bigger and never came back" actually was.
  //
  // Conclusion: no CSS height unit is trustworthy here. While the panel is
  // open we size it to visualViewport ALWAYS — keyboard up or down — because
  // that is the only value that equals what the customer can see.
  function syncPanelViewport() {
    if (!IS_MOBILE || !state.open) return;
    var vv = window.visualViewport;
    if (!vv || !vv.height) return;
    var wasNear = nearBottom();
    panel.style.height = vv.height + "px";
    panel.style.maxHeight = vv.height + "px";
    panel.style.top = vv.offsetTop + "px";
    panel.style.bottom = "auto";
    if (wasNear) scrollBottom();
  }
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", syncPanelViewport);
    window.visualViewport.addEventListener("scroll", syncPanelViewport);
  }

  function openPanel() {
    if (state.open) return;
    state.open = true;
    panel.style.display = "flex";
    syncPanelViewport();
    resyncSoon();
    clearBadge(); hideTip();
    if (!msgs.children.length) firstOpen();
    if (!IS_MOBILE) setTimeout(function () { input.focus(); }, 100);
  }
  function closePanel() {
    state.open = false;
    panel.style.display = "none";
    // drop the inline sizing so the stylesheet governs the next open
    panel.style.height = "";
    panel.style.maxHeight = "";
    panel.style.top = "";
    panel.style.bottom = "";
    // nothing to restore: inline sizing is cleared above
  }
  bubble.addEventListener("click", function () { state.open ? closePanel() : openPanel(); });
  $(".hb-close", panel).addEventListener("click", closePanel);

  var ACCESSORY_CHIPS = [
    "📱 Phone Cases", "🔌 Chargers & Adapters", "🛡️ Screen Protectors",
    "📷 Camera Lens Protectors", "🌀 Cable Protectors", "📱 Mobile Stands",
    "⌚ Watch Straps", "🖼️ Posters"
  ];
  function accessoriesMenu() {
    addMsg("bot", "What are you looking for? 👇");
    addChips(ACCESSORY_CHIPS, function (pick) {
      var clean = pick.replace(/^[^\w]+\s*/, "");
      if (clean === "Phone Cases") {
        send("Show me phone cases for my phone");
      } else {
        send("Show me " + clean.toLowerCase());
      }
    }, true);
  }
  function isAccessoriesLabel(label) {
    return /^find\b/i.test(label || "") && /accessor/i.test(label || "");
  }

  function showWelcomeChips() {
    var welcome = (state.config && state.config.welcome) || {};
    if (welcome.quick_replies && welcome.quick_replies.length) {
      addChips(welcome.quick_replies, function (pick) {
        if (isAccessoriesLabel(pick)) accessoriesMenu(); else send(pick);
      }, true);
    }
  }

  var firstOpenTries = 0, firstOpenPending = false;
  function firstOpen() {
    if (!state.bootFailed && !state.sessionKey && firstOpenTries < 15) {
      if (firstOpenPending) return;
      firstOpenPending = true;
      firstOpenTries += 1;
      setTimeout(function () {
        firstOpenPending = false;
        if (!msgs.children.length) firstOpen();
      }, 400);
      return;
    }
    if (state.bootFailed || !state.sessionKey) {
      addMsg("bot", "I'm having trouble connecting right now 🙈 Please refresh the page and try again.");
      return;
    }
    // Deferred history (see boot). Fetch once, on the first open of a resumed
    // session, then re-enter to render. Costs one request for the ~1.3% who
    // open the chat instead of one on every page view for everyone.
    if (state.canResume && !state.historyLoaded) {
      state.historyLoaded = true;
      fetch(BACKEND + "/session/history?session_key=" + encodeURIComponent(state.sessionKey))
        .then(function (r) { return r.json(); })
        .then(function (h) { if (h.messages && h.messages.length) state.history = h.messages; })
        .catch(function () {})
        .then(function () { msgs.innerHTML = ""; firstOpen(); });
      return;
    }
    if (state.history && state.history.length) {
      state.history.forEach(function (m) {
        addMsg(m.role === "user" ? "user" : "bot", m.content);
      });
      state.devicePromptShown = true;
      var rpw = (state.config && state.config.product_welcome) || {};
      if (rpw.enabled !== false && productName()) {
        var rchips = rpw.chips && rpw.chips.length ? rpw.chips
          : ["What's the material like?", "When will I receive it?", "Show me similar designs"];
        addChips(rchips, function (pick) { send(pick); }, true);
        addPageProductButton();
      } else {
        showWelcomeChips();
      }
      scrollBottom();
      return;
    }
    var pw = (state.config && state.config.product_welcome) || {};
    var pn = productName();
    if (pw.enabled !== false && pn && location.pathname.indexOf("/products/") !== -1) {
      var short = pn.length > 60 ? pn.slice(0, 57) + "…" : pn;
      var ptext = (pw.text || "**Checking out {product}?** Ask me anything! 👇")
        .replace(/\{product\}/g, short);
      addMsg("bot", ptext);
      var pchips = pw.chips && pw.chips.length ? pw.chips
        : ["What's the material like?", "When will I receive it?", "Show me similar designs"];
      addChips(pchips, function (pick) { send(pick); }, true);
      addPageProductButton();
      state.devicePromptShown = true;
      return;
    }
    var welcome = (state.config && state.config.welcome) || {};
    addMsg("bot", welcome.greeting || "Hi! Ask me anything about products or your order.");
    if (state.devicePrompt && !state.devicePromptShown) {
      state.devicePromptShown = true;
      addMsg("bot", state.devicePrompt);
      addChips(["Yes, show me!", "No, different phone"], function (pick) {
        if (pick.indexOf("Yes") === 0) {
          api("/session/device", { session_key: state.sessionKey, confirmed: true })
            .catch(function () {});
          send("Show me cases for my phone");
        } else {
          api("/session/device", { session_key: state.sessionKey, confirmed: false })
            .catch(function () {});
          addMsg("bot", "No problem! Type your phone's brand and model (e.g. \"Vivo V40\") and I'll find everything that fits!");
          showWelcomeChips();
        }
      });
      showWelcomeChips();
    } else {
      showWelcomeChips();
    }
  }

  // ---------- nudges ----------
  var tip = null;

  // Once-per-visitor flags, but with a 7-DAY LIFE rather than forever.
  // v1.2 read these as plain localStorage keys, which meant a visitor who saw
  // the exit modal once never saw it again on that browser — the addressable
  // audience shrank every single day and the slot looked "low volume" when it
  // was actually being suppressed. Legacy "1" values are treated as expired so
  // everyone who has been locked out becomes eligible again on this deploy.
  var FLAG_TTL_MS = 7 * 24 * 3600 * 1000;
  function flagActive(key) {
    try {
      var v = localStorage.getItem(key);
      if (!v) return false;
      if (v === "1") { localStorage.removeItem(key); return false; }  // legacy = expired
      var t = Number(v);
      if (!t || Date.now() - t > FLAG_TTL_MS) { localStorage.removeItem(key); return false; }
      return true;
    } catch (e) { return false; }
  }
  function flagSet(key) {
    try { localStorage.setItem(key, String(Date.now())); } catch (e) {}
  }

  function lastNudgeAt() {
    try { return Number(sessionStorage.getItem("hb_nudge_ts") || 0); } catch (e) { return state.lastNudgeAt || 0; }
  }
  function markNudge() {
    state.lastNudgeAt = Date.now();
    try { sessionStorage.setItem("hb_nudge_ts", String(Date.now())); } catch (e) {}
  }
  function showTip(text, onClick) {
    if (document.querySelector(".hb-overlay")) return;
    try {
      var cts = Number(sessionStorage.getItem("hb_cart_click_ts") || 0);
      if (cts && Date.now() - cts < 600000) return;
    } catch (e) {}
    if (Date.now() - lastNudgeAt() < 30000) return;
    markNudge();
    hideTip();
    tip = el("div", "hb-tip", esc(text) + '<span class="hb-x">×</span>');
    tip.addEventListener("click", function (e) {
      if (e.target.className === "hb-x") { hideTip(); setBadge(1); }
      else { hideTip(); if (typeof onClick === "function") onClick(); else openPanel(); }
    });
    document.body.appendChild(tip);
    setTimeout(function () { if (tip) { hideTip(); setBadge(1); } }, 12000);
  }
  function hideTip() { if (tip) { tip.remove(); tip = null; } }
  function appendCartButton() {
    fetch("/cart.js").then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || cart.item_count === 0) return;
        var co = el("div", "hb-checkout-wrap",
          '<button class="hb-checkoutbtn">🛒 Go to Cart & Checkout — ₹' +
          Math.round(cart.total_price / 100) + '</button>');
        $(".hb-checkoutbtn", co).addEventListener("click", goToCart);
        msgs.appendChild(co);
        scrollBottom();
      }).catch(function () {});
  }
  function cartGross(c) {
    if (!c) return 0;
    var v = c.original_total_price != null ? c.original_total_price
          : (c.items_subtotal_price != null ? c.items_subtotal_price : c.total_price);
    return v != null ? v / 100 : 0;
  }
  function goToCart() {
    closePanel();
    var sels = '#cart-icon-bubble, .header__icon--cart, [data-cart-drawer-toggle], ' +
               '[data-drawer-toggle*="cart" i], .cart-toggle, .js-drawer-open-cart, ' +
               'a[href*="/cart"], [aria-label*="cart" i]';
    var cands = [];
    document.querySelectorAll(sels).forEach(function (el_) {
      if (!el_ || el_.closest("#hatke-bot-root")) return;
      if (el_.offsetParent === null) return;
      var r = el_.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return;
      cands.push({ el: el_, top: Math.abs(r.top) });
    });
    if (cands.length) {
      cands.sort(function (a, b) { return a.top - b.top; });
      cands[0].el.click();
      return;
    }
    window.location.href = "/cart";
  }
  function openOffersFlow() {
    openPanel();
    state.cartBtnAfterReply = true;
    send("What offers and discounts do I get?");
  }
  function setBadge(n) {
    if (state.open) return;
    if (!badge) { badge = el("span", "hb-badge"); bubble.appendChild(badge); }
    badge.textContent = n;
  }
  function clearBadge() { if (badge) { badge.remove(); badge = null; } }

  function matchPage(pattern, path) {
    if (pattern === path) return true;
    if (pattern.slice(-1) === "*") return path.indexOf(pattern.slice(0, -1)) === 0;
    return false;
  }

  function adSource() {
    try {
      var qs = new URLSearchParams(location.search);
      var src = qs.get("utm_source");
      if (src) { sessionStorage.setItem("hb_utm", src.toLowerCase()); return src.toLowerCase(); }
      return sessionStorage.getItem("hb_utm") || null;
    } catch (e) { return null; }
  }

  function loadPageProduct() {
    if (location.pathname.indexOf("/products/") === -1) return;
    fetch(location.pathname.split("?")[0] + ".js")
      .then(function (r) { return r.json(); })
      .then(function (p) {
        if (p && p.variants) state.pageProduct = p;
      })
      .catch(function () {});
  }

  function addPageProductButton() {
    var p = state.pageProduct;
    if (!p) return;
    var v = null;
    for (var i = 0; i < p.variants.length; i++) {
      if (p.variants[i].available) { v = p.variants[i]; break; }
    }
    if (!v) return;
    var price = Math.round((v.price || p.price || 0) / 100);
    var row = el("div", "hb-checkout-wrap",
      '<button class="hb-checkoutbtn">🛒 Add this to cart — ₹' + price + '</button>');
    $(".hb-checkoutbtn", row).addEventListener("click", function () {
      var btn = this;
      btn.textContent = "Adding…"; btn.disabled = true;
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: v.id, quantity: 1 }),
      })
        .then(function (r) { if (!r.ok) throw 0; return fetch("/cart.js"); })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          btn.textContent = "Added ✓";
          tagCart("direct");
          onCartChanged(cart, { title: p.title, product_type: p.type || null });
        })
        .catch(function () { btn.textContent = "Couldn't add — try the page button"; });
    });
    msgs.appendChild(row);
    scrollBottom();
  }

  function productName() {
    if (location.pathname.indexOf("/products/") === -1) return null;
    var og = $('meta[property="og:title"]');
    var t = (og ? og.getAttribute("content") : document.title) || "";
    return t.split("|")[0].split("–")[0].trim() || null;
  }

  // ---------- timed (idle) modal ----------
  function setupIdleModal() {
    if (!slotOn("idle")) return;
    var cfg = (state.config && state.config.idle_modal) || {};
    if (!cfg.enabled) return;
    var excl = cfg.exclude_pages || ["/cart"];
    var path = location.pathname;
    for (var i = 0; i < excl.length; i++) {
      if (path === excl[i] || path.indexOf(excl[i] + "/") === 0) return;
    }
    if (cfg.once_per_visitor !== false && flagActive("hb_modal_shown")) return;

    var idleDefaults = {
      title: cfg.title || "Hey there! 👋",
      text: cfg.text || "Need help finding the perfect product for your phone?",
      cta: cfg.cta || "Yes please!",
    };
    var landedAt = Date.now();
    try {
      var st = Number(sessionStorage.getItem("hb_landed_ts") || 0);
      if (st) { landedAt = st; } else { sessionStorage.setItem("hb_landed_ts", String(landedAt)); }
    } catch (e) {}
    var waitMs = Math.max(1500, (cfg.idle_s || 30) * 1000 - (Date.now() - landedAt));
    var markIdleShown = function () {
      if (cfg.once_per_visitor !== false) flagSet("hb_modal_shown");
    };
    setTimeout(function () {
      if (state.open) return;
      fetch("/cart.js").then(function (r) { return r.json(); })
        .then(function (cart) {
          if (cart && cart.item_count > 0) return;
          modalWithRetry("idle", idleDefaults, null, markIdleShown);
        })
        .catch(function () { modalWithRetry("idle", idleDefaults, null, markIdleShown); });
    }, waitMs);
  }

  // ---- CAMPAIGNS ----
  function trackNudge(camp, variant, slot, event) {
    try {
      if (!state.sessionKey) return;
      fetch(BACKEND + "/event/nudge", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_key: state.sessionKey, campaign_id: camp,
                               variant_id: variant, slot: slot, event: event }),
      }).catch(function () {});
    } catch (e) { console.log("[hb-nudge] track failed", e); }
  }
  function campaignFor(slot, defaults) {
    var list = ((state.config && state.config.campaigns) || {}).list || [];
    var camp = null;
    for (var i = 0; i < list.length; i++) {
      if (list[i].enabled !== false && list[i].slot === slot &&
          (list[i].variants || []).length >= 2) { camp = list[i]; break; }
    }
    if (!camp) return { cfg: defaults, campaign_id: null, variant_id: null };
    var h = 0, k = state.sessionKey || String(Math.random());
    for (var j = 0; j < k.length; j++) h = (h * 31 + k.charCodeAt(j)) >>> 0;
    var v = camp.variants[h % camp.variants.length];
    return {
      cfg: { title: v.title || defaults.title,
             text: v.text || defaults.text,
             cta: v.cta || defaults.cta },
      campaign_id: camp.id, variant_id: v.id || String(h % camp.variants.length),
    };
  }
  function showModalSlot(slot, defaults, onCta, variantSuffix) {
    var pick = campaignFor(slot, defaults);
    var force = (slot === "cart_drawer" || slot === "cart_leave" || slot === "exit"
                 || slot === "checkout_return");
    // One slot can carry two messages chosen at fire time (cart_drawer says
    // something different to someone who reached checkout). The suffix keeps
    // them apart in the results table so each can be measured on its own.
    var vid = (pick.variant_id || "") + (variantSuffix || "");
    var fired = showModal(pick.cfg, function () {
      if (pick.campaign_id) trackNudge(pick.campaign_id, vid, slot, "click");
      if (typeof onCta === "function") onCta(); else openPanel();
    }, force);
    if (fired && pick.campaign_id) trackNudge(pick.campaign_id, vid, slot, "impression");
    return fired;
  }
  var SLOT_FLAGS = { cart_drawer: "hb_cartleave", cart_leave: "hb_cartleave",
                     exit: "hb_exit_shown" };
  function slotDone(slot) {
    try {
      if (slot === "idle") return flagActive("hb_modal_shown");
      var f = SLOT_FLAGS[slot];
      if (f === "hb_exit_shown") return flagActive(f);
      return f ? !!sessionStorage.getItem(f) : false;
    } catch (e) { return false; }
  }
  function modalWithRetry(slot, defaults, onCta, onShown, tries, srcTag, variantSuffix) {
    if (!slotOn(slot)) return;  // master switch — slot disabled server-side
    tries = tries == null ? 10 : tries;
    if (slotDone(slot)) { console.log("[hb-nudge]", slot, srcTag || "?", "abort: flag set"); return; }
    if (document.querySelector(".hb-overlay") || state.open) {
      setTimeout(function () {
        modalWithRetry(slot, defaults, onCta, onShown, tries, srcTag, variantSuffix);
      }, 5000);
      return;
    }
    var ok = showModalSlot(slot, defaults, onCta, variantSuffix);
    console.log("[hb-nudge]", slot, srcTag || "?",
      ok ? "SHOWN: " + (defaults.title || "") : "suppressed, retries left " + tries);
    if (ok) {
      if (typeof onShown === "function") onShown();
      return;
    }
    if (tries > 0) setTimeout(function () {
      modalWithRetry(slot, defaults, onCta, onShown, tries - 1, srcTag, variantSuffix);
    }, 6000);
  }

  function showModal(cfg, onCta, force) {
    if (state.open) { console.log("[hb-modal] no: chat open"); return false; }
    if (document.querySelector(".hb-overlay")) { console.log("[hb-modal] no: modal already on screen"); return false; }
    if (!force && Date.now() - lastNudgeAt() < 30000) { console.log("[hb-modal] no: cooldown"); return false; }
    var shown = 0;
    try { shown = Number(sessionStorage.getItem("hb_modals") || 0); } catch (e) {}
    if (shown >= 2) { console.log("[hb-modal] no: session cap (" + shown + ")"); return false; }
    try {
      var lm = JSON.parse(sessionStorage.getItem("hb_last_modal") || "{}");
      if (lm.t === cfg.title && Date.now() - (lm.at || 0) < 90000) {
        console.log("[hb-modal] no: same modal shown " + Math.round((Date.now() - lm.at) / 1000) + "s ago");
        return false;
      }
      sessionStorage.setItem("hb_last_modal",
        JSON.stringify({ t: cfg.title, at: Date.now() }));
    } catch (e) {}
    try { sessionStorage.setItem("hb_modals", String(shown + 1)); } catch (e) {}
    markNudge();
    hideTip();
    var overlay = el("div", "hb-overlay");
    overlay.innerHTML =
      '<div class="hb-modal"><button class="hb-mx" aria-label="Close">×</button>' +
      '<h3>' + esc(cfg.title || "Hey there! 👋") + '</h3>' +
      '<p>' + esc(cfg.text || "Need help finding the perfect product for your phone?") + '</p>' +
      '<button class="hb-mcta">' + esc(cfg.cta || "Yes please!") + '</button></div>';
    function dismiss() { overlay.remove(); setBadge(1); }
    $(".hb-mx", overlay).addEventListener("click", dismiss);
    overlay.addEventListener("click", function (e) { if (e.target === overlay) dismiss(); });
    $(".hb-mcta", overlay).addEventListener("click", function () {
      overlay.remove();
      if (typeof onCta === "function") onCta(); else openPanel();
    });
    document.body.appendChild(overlay);
    return true;
  }

  // NUDGE: exit intent (desktop) — the ONE nudge on by default
  function setupExitIntent() {
    if (!slotOn("exit")) return;
    var cfg = (state.config && state.config.exit_modal) || {};
    if (cfg.enabled === false) return;
    if (cfg.once_per_visitor !== false && flagActive("hb_exit_shown")) return;
    var armed = false;
    setTimeout(function () { armed = true; }, (cfg.min_seconds_on_site || 15) * 1000);
    document.addEventListener("mouseout", function handler(e) {
      if (!armed || state.open) return;
      if (e.clientY > 0 || e.relatedTarget) return;
      document.removeEventListener("mouseout", handler);
      var markExitShown = function () { flagSet("hb_exit_shown"); };
      fetch("/cart.js").then(function (r) { return r.json(); })
        .then(function (cart) {
          if (cart && cart.item_count > 0) {
            var total = cartGross(cart);
            var ladder = ((state.config && state.config.discount_ladder) || {}).tiers || [];
            ladder = ladder.slice().sort(function (a, b) { return a.threshold - b.threshold; });
            var nxt = null;
            for (var i = 0; i < ladder.length; i++) {
              if (total < ladder[i].threshold) { nxt = ladder[i]; break; }
            }
            var line = "You have " + cart.item_count + " item" + (cart.item_count > 1 ? "s" : "") +
                       " (₹" + Math.round(total) + ") waiting. ";
            line += nxt
              ? "Just ₹" + Math.round(nxt.threshold - total) + " more unlocks " + nxt.percent + "% off — and prepaid orders get an extra 10% off!"
              : "Pay online (prepaid) and get 10% off the whole order!";
            modalWithRetry("exit", {
              title: "Your cart misses you! 🛒",
              text: line,
              cta: "Complete my order",
            }, goToCart, markExitShown);
          } else {
            modalWithRetry("exit", {
              title: cfg.title || "Wait! 💳 Get 10% OFF",
              text: cfg.text || "Pay online (prepaid) and get 10% off your entire order!",
              cta: cfg.cta || "Show me all the offers",
            }, function () {
              openPanel();
              send(cfg.cta_message || "What offers and discounts do I get?");
            }, markExitShown);
          }
        })
        .catch(function () {
          modalWithRetry("exit", {
            title: cfg.title || "Wait! 💳 Get 10% OFF",
            text: cfg.text || "Pay online (prepaid) and get 10% off your entire order!",
            cta: cfg.cta || "Show me all the offers",
          }, function () {
            openPanel();
            send(cfg.cta_message || "What offers and discounts do I get?");
          }, markExitShown);
        });
    });
  }

  // NUDGE: repeat visitor — cart snapshot tracking always runs; modal gated
  function setupRepeatVisitor() {
    var now = Date.now();
    var last = 0;
    try {
      last = Number(localStorage.getItem("hb_last_visit") || 0);
      localStorage.setItem("hb_last_visit", String(now));
    } catch (e) { return; }
    fetch("/cart.js").then(function (r) { return r.json(); })
      .then(function (c) {
        try {
          if (!sessionStorage.getItem("hb_cart_snap")) {
            sessionStorage.setItem("hb_cart_snap", "1");
            var pending = Number(localStorage.getItem("hb_last_cart") || 0);
            if (!(pending > 0 && c && c.item_count === 0)) {
              localStorage.setItem("hb_last_cart", String(c ? c.item_count : 0));
            }
          }
        } catch (e) {}
      }).catch(function () {});
    if (!slotOn("repeat")) return;
    if (!last) return;
    var gapH = (now - last) / 36e5;
    if (gapH < 0.5 || gapH > 24 * 14) return;
    try {
      if (sessionStorage.getItem("hb_rv_shown")) return;
      sessionStorage.setItem("hb_rv_shown", "1");
    } catch (e) {}
    setTimeout(function () {
      if (state.open) return;
      fetch("/cart.js").then(function (r) { return r.json(); })
        .then(function (cart) {
          var hasCart = cart && cart.item_count > 0;
          var prevCart = 0;
          try {
            prevCart = Number(localStorage.getItem("hb_last_cart") || 0);
            localStorage.setItem("hb_last_cart", String(cart ? cart.item_count : 0));
          } catch (e) {}
          if (prevCart > 0 && !hasCart) return;
          modalWithRetry("repeat", {
            title: "Welcome back! 👋",
            text: hasCart
              ? "Your cart is still waiting — want to see the offers you can unlock?"
              : "Good to see you again! Need help picking something for your phone?",
            cta: hasCart ? "Show me the offers" : "Yes, help me pick!",
          }, hasCart ? openOffersFlow : function () { openPanel(); }, null, 5);
        }).catch(function () {});
    }, 6000);
  }

  // NUDGE: product-page dwell
  function setupDwellNudge() {
    if (!slotOn("dwell")) return;
    if (location.pathname.indexOf("/products/") === -1) return;
    var key = "hb_dwell_" + location.pathname;
    try { if (sessionStorage.getItem(key)) return; } catch (e) {}
    setTimeout(function () {
      if (state.open) return;
      if (state.cartTag === "direct" || (state.milestoneEls && state.milestoneEls.length)) return;
      fetch("/cart.js").then(function (r) { return r.json(); })
        .then(function (cart) {
          if (cart && cart.item_count > 0) return;
          var pn = productName();
          modalWithRetry("dwell", {
            title: "Torn on this one? 🤔",
            text: (pn ? "Still thinking about " + (pn.length > 40 ? pn.slice(0, 37) + "…" : pn) + "? " : "") +
                  "Ask me anything — material, delivery, offers!",
            cta: "Chat with Riya",
          }, function () { openPanel(); }, function () {
            try { sessionStorage.setItem(key, "1"); } catch (e) {}
          }, 6);
        }).catch(function () {});
    }, 30000);
  }

  // NUDGE: left the cart page without checking out
  function setupCartLeaveNudge() {
    if (!slotOn("cart_leave")) return;
    if (location.pathname === "/cart" || location.pathname.indexOf("/cart/") === 0) return;
    var ref = "";
    try { ref = new URL(document.referrer).pathname; } catch (e) { return; }
    if (ref !== "/cart" && ref.indexOf("/cart/") !== 0) return;
    try {
      if (sessionStorage.getItem("hb_cartleave")) return;
    } catch (e) {}
    setTimeout(function () {
      if (state.open) return;
      fetch("/cart.js").then(function (r) { return r.json(); })
        .then(function (cart) {
          if (!cart || cart.item_count === 0) return;
          modalWithRetry("cart_leave", {
            title: "Your cart is waiting! 🛒",
            text: "Need help with offers?",
            cta: "Show me the offers",
          }, openOffersFlow, function () {
            try { sessionStorage.setItem("hb_cartleave", "1"); } catch (e) {}
          });
        }).catch(function () {});
    }, 2500);
  }

  // NUDGE: cart drawer closed with items. The checkout_reached funnel event
  // lives here too, so this ALWAYS runs — only the modal is gated.
  function setupCartDrawerNudge() {
    var fired = false;
    try { if (sessionStorage.getItem("hb_cartleave")) fired = true; } catch (e) {}

    function isCartOverlay(node) {
      if (!node || node.nodeType !== 1 || node.closest("#hatke-bot-root")) return false;
      var st = getComputedStyle(node);
      if (st.display === "none" || st.visibility === "hidden") return false;
      var r = node.getBoundingClientRect();
      var big = r.width > innerWidth * 0.6 && r.height > innerHeight * 0.6;
      if (node.tagName === "IFRAME") {
        if (!big) return false;
        var src = (node.src || "").toLowerCase();
        return /cart|checkout|shiprocket|kwik|drawer/.test(src);
      }
      if (st.position !== "fixed" && st.position !== "absolute") return false;
      if (big) return true;
      var id = ((node.id || "") + " " + (node.className || "")).toLowerCase();
      return /cart|drawer/.test(id) && r.width > 200 && r.height > 300 &&
             r.right > 0 && r.left < innerWidth;
    }

    var ckProbing = false;
    // TRUE CHECKOUT DETECTION.
    // maybeCheckoutReached used to fire on ANY large cart overlay and on any
    // cart-icon click, so "reached checkout" really meant "opened the cart":
    // 704 of 724 cart-drawer nudges carried the flag, which is every cart-open
    // rather than the ~5% who genuinely get to payment.
    //
    // A real checkout on this store is an IFRAME whose src is the checkout
    // host. The cart drawer is a div, and the cart iframe (if any) has /cart in
    // the src without a checkout path — so the src pattern here is deliberately
    // narrower than isCartOverlay's, which still wants to catch both.
    // The checkout on this store is FASTRR BY PICKRR, not a URL containing
    // "shiprocket": the frame is fastrr-boost-ui.pickrr.com. The earlier
    // pattern never matched it, so reached_checkout sat at ~1 a day.
    //
    // Confirmed from the live page (24 Sep 2026), whose iframes are:
    //   fastrr-boost-ui.pickrr.com/?prefetch=true   <- checkout, PREFETCHED
    //   sr-cdn.shiprocket.in/sr-promise/...         <- delivery-promise badge
    //   google.com/shopping/merchantverse, web-pixels sandboxes
    //
    // Two traps this has to survive:
    //  1. The Fastrr frame is PRESENT ON EVERY PAGE, prefetched and hidden. So
    //     matching the URL alone would mark every session as reaching
    //     checkout — the 97% over-count all over again. The visibility and
    //     size checks in anyCheckoutFrameOpen are what make it real.
    //  2. sr-promise DOES contain "shiprocket" but is a small badge on product
    //     pages. It is excluded by size, and now also by name.
    function isCheckoutFrame(node) {
      if (!node || node.tagName !== "IFRAME") return false;
      var src = (node.src || "").toLowerCase();
      if (!src) return false;
      if (src.indexOf("sr-promise") !== -1) return false;   // delivery badge
      return /fastrr|pickrr|checkout|kwikcheckout|razorpay|payment/.test(src);
    }

    function anyCheckoutFrameOpen() {
      var frames = document.querySelectorAll("iframe");
      for (var i = 0; i < frames.length; i++) {
        if (!isCheckoutFrame(frames[i])) continue;
        var st = getComputedStyle(frames[i]);
        if (st.display === "none" || st.visibility === "hidden") continue;
        var r = frames[i].getBoundingClientRect();
        if (r.width > innerWidth * 0.5 && r.height > innerHeight * 0.4) return true;
      }
      return false;
    }

    function maybeCheckoutReached() {
      if (ckProbing || !state.sessionKey) return;
      try { if (sessionStorage.getItem("hb_checkout_reached")) return; } catch (e) {}
      // Only a visible checkout iframe counts. Opening the cart drawer does not.
      if (!anyCheckoutFrameOpen()) return;
      ckProbing = true;
      fetch("/cart.js").then(function (r) { return r.json(); })
        .then(function (cart) {
          if (!cart || cart.item_count === 0) return;
          return api("/event/funnel", {
            session_key: state.sessionKey,
            event: "checkout_reached",
            meta: { cart_total: cartGross(cart) },
          }).then(function () {
            try {
              sessionStorage.setItem("hb_checkout_reached", "1");
              // timestamp so setupCheckoutReturnNudge can require a real gap
              // before treating a later page view as an abandonment
              sessionStorage.setItem("hb_checkout_reached_ts", String(Date.now()));
            } catch (e) {}
          });
        })
        .catch(function () {})
        .then(function () { ckProbing = false; });
    }

    // ---- Two paths to the nudge:
    //   overlay path  — we SAW the drawer close, so fire immediately. No guess
    //                   is involved and waiting only loses the moment.
    //   click path    — we never saw a drawer (Shiprocket renders outside the
    //                   queryable DOM on some templates), so we infer from a
    //                   cart-icon click and wait for a quiet period before
    //                   interrupting. Activity resets that clock.
    function idleMs() {
      var sl = (state.config && state.config.nudge_slots) || {};
      var s = Number(sl.cart_drawer_idle_s);
      return (s > 0 ? s : 10) * 1000;
    }
    var idleTimer = null, idleArmed = false;
    function fireCartNudge(tag) {
      idleArmed = false;
      clearTimeout(idleTimer);
      if (fired || state.open || !slotOn("cart_drawer")) return;
      fetch("/cart.js").then(function (r) { return r.json(); })
        .then(function (cart) {
          if (fired || state.open || !cart || cart.item_count === 0) return;

          // CHECKOUT-AWARE COPY.
          // On this store closing checkout drops you back into the cart
          // drawer, so this one moment covers both "looked at my cart" and
          // "tried to pay and stopped". A separate checkout_return slot could
          // never fire: cart_drawer always got there first and consumed the
          // session's modal budget.
          //
          // So the trigger stays exactly as it was and only the WORDS change.
          // Someone who reached checkout gets asked what went wrong — that is
          // the one question worth asking at this moment, and the answer is
          // information we currently never hear. Everyone else gets offers.
          var sawCheckout = false;
          try { sawCheckout = !!sessionStorage.getItem("hb_checkout_reached"); } catch (e) {}

          var defaults = sawCheckout
            ? { title: "Anything stop you at checkout?",
                text: "If something didn't work — payment, delivery or COD — "
                      + "tell me and I'll sort it out right now.",
                cta: "I had a problem" }
            : { title: "Wait! 🛒",
                text: "Need help with offers?",
                cta: "Show me the offers" };

          var onCta = sawCheckout
            ? function () {
                openPanel();
                send("I tried to check out but didn't complete the order.");
              }
            : openOffersFlow;

          modalWithRetry("cart_drawer", defaults, onCta, function () {
            fired = true;
            try {
              sessionStorage.setItem("hb_cartleave", "1");
              sessionStorage.removeItem("hb_cart_click_ts");
            } catch (e) {}
          }, null, tag + (sawCheckout ? "/after-checkout" : ""),
             sawCheckout ? "-checkout" : "-offers");
        }).catch(function () {});
    }
    function armIdle(tag) {
      if (fired || !slotOn("cart_drawer")) return;
      idleArmed = true;
      clearTimeout(idleTimer);
      idleTimer = setTimeout(function () { fireCartNudge(tag); }, idleMs());
    }
    function onActivity() {
      if (!idleArmed) return;
      if (document.querySelector(".hb-overlay")) return;
      clearTimeout(idleTimer);
      idleTimer = setTimeout(function () { fireCartNudge("idle-after-cart"); }, idleMs());
    }
    ["scroll", "touchstart", "pointerdown", "keydown", "wheel"].forEach(function (ev) {
      window.addEventListener(ev, onActivity, { passive: true, capture: true });
    });

    var wasOpen = false;
    function check() {
      // Poll for a checkout iframe on EVERY tick, not just when the cart
      // overlay first opens. Checkout appears after the customer clicks
      // through from inside the drawer, by which time wasOpen is already true
      // — gating on the transition would mean real checkouts were never seen.
      // The call early-returns once the flag is set or when no checkout frame
      // is present, so this is cheap.
      maybeCheckoutReached();
      var open = false;
      var cands = document.querySelectorAll(
        "iframe,[id*=cart i],[class*=cart],[id*=drawer i],[class*=drawer]");
      for (var i = 0; i < cands.length; i++) {
        if (isCartOverlay(cands[i])) { open = true; break; }
      }
      if (open) {
        wasOpen = true;
        clearTimeout(idleTimer); idleArmed = false;  // cart is open — no countdown
        return;
      }
      if (wasOpen && !open) {
        wasOpen = false;
        // We watched it close — no need to guess, and no need to wait.
        fireCartNudge("overlay-path");
      }
    }
    var mo = new MutationObserver(function () { check(); });
    mo.observe(document.body, { childList: true, subtree: true,
                                attributes: true,
                                attributeFilter: ["class", "style", "aria-hidden", "open", "src"] });
    setInterval(check, 1200);

    document.addEventListener("click", function (e) {
      var t = e.target && e.target.closest &&
              e.target.closest('a[href*="/cart"], #cart-icon-bubble, [data-cart-drawer-toggle], .cart-toggle, [aria-label*="cart" i], .header__icon--cart');
      if (t && !t.closest("#hatke-bot-root")) {
        try { sessionStorage.setItem("hb_cart_click_ts", String(Date.now())); } catch (err) {}
        // NB: no maybeCheckoutReached() here. Clicking the cart icon opens the
        // DRAWER — checkout is one step further on, and counting this click as
        // "reached checkout" is exactly what made 97% of sessions look like
        // checkout visits. The poll in check() picks up the real thing.
      }
    }, true);
    function pointInOverlay(x, y) {
      var el_ = document.elementFromPoint(x, y);
      var d = 0;
      while (el_ && el_.nodeType === 1 && d < 5) {
        if (el_ !== document.documentElement && el_ !== document.body) {
          var r = el_.getBoundingClientRect();
          if (r.width > innerWidth * 0.6 && r.height > innerHeight * 0.6) return true;
        }
        el_ = el_.parentElement; d += 1;
      }
      return false;
    }
    function armedCheck(e) {
      if (!slotOn("cart_drawer")) return;
      if (fired || state.open || wasOpen) return;
      if (e && e.clientX != null &&
          pointInOverlay(e.clientX, e.clientY)) return;
      var ts = 0;
      try { ts = Number(sessionStorage.getItem("hb_cart_click_ts") || 0); } catch (e) {}
      if (!ts) return;
      var age = Date.now() - ts;
      if (age < 3000) return;
      if (age > 600000) {
        try { sessionStorage.removeItem("hb_cart_click_ts"); } catch (e) {}
        return;
      }
      if (document.hidden) return;
      try { sessionStorage.removeItem("hb_cart_click_ts"); } catch (e) {}
      // customer is back on the page after the cart: start the quiet-period
      // countdown. The nudge only shows if they then do nothing for N seconds.
      armIdle("click-path");
    }
    ["pointerdown", "mousedown"].forEach(function (ev) {
      document.addEventListener(ev, function (e) { armedCheck(e); },
                                { capture: true, passive: true });
    });
    document.addEventListener("touchstart", function (e) {
      var t = e.touches && e.touches[0];
      armedCheck(t ? { clientX: t.clientX, clientY: t.clientY } : null);
    }, { capture: true, passive: true });
    var bootTs = Date.now();
    setTimeout(function () {
      var ts = 0;
      try { ts = Number(sessionStorage.getItem("hb_cart_click_ts") || 0); } catch (e) {}
      if (ts && ts < bootTs - 1000) armedCheck();
    }, 3000);
    window.__hbCart = function () {
      var ts = 0;
      try { ts = Number(sessionStorage.getItem("hb_cart_click_ts") || 0); } catch (e) {}
      return { fired: fired, wasOpen: wasOpen, open_panel: state.open,
               hidden: document.hidden,
               click_age_s: ts ? Math.round((Date.now() - ts) / 1000) : null,
               cartleave: (function () { try { return sessionStorage.getItem("hb_cartleave"); } catch (e) { return "?"; } })(),
               modals: (function () { try { return sessionStorage.getItem("hb_modals"); } catch (e) { return "?"; } })() };
    };
  }

  // NUDGE: came back after reaching checkout without ordering.
  //
  // This is the biggest hole in the funnel and nothing else covers it: over 30
  // days 11,705 sessions reached checkout and 972 ordered. Everyone else came
  // back to the site with items still in their cart and was never asked why.
  //
  // The signal is already recorded — setupCartDrawerNudge writes
  // hb_checkout_reached the moment the cart/checkout overlay opens. Here we
  // wait for a LATER page view where that flag is set and the cart is STILL
  // not empty: a completed order clears the Shopify cart, so a surviving cart
  // means they did not buy.
  //
  // The CTA is deliberately "I had a problem" rather than a discount. Offering
  // money to someone who was already buying costs margin for nothing, and this
  // way every click turns an invisible loss into a logged reason.
  function setupCheckoutReturnNudge() {
    if (!slotOn("checkout_return")) return;
    var reachedAt = 0;
    try {
      if (!sessionStorage.getItem("hb_checkout_reached")) return;
      if (sessionStorage.getItem("hb_checkout_return_shown")) return;
      reachedAt = Number(sessionStorage.getItem("hb_checkout_reached_ts") || 0);
    } catch (e) { return; }

    // At least a minute since checkout opened. Someone flicking the drawer
    // open and shut has not abandoned anything, and on templates where the
    // checkout is an in-page overlay the close would otherwise read as a
    // return.
    if (reachedAt && Date.now() - reachedAt < 60000) return;

    setTimeout(function () {
      if (state.open) return;
      fetch("/cart.js").then(function (r) { return r.json(); })
        .then(function (cart) {
          // Empty cart = they ordered (or cleared it). Nothing to ask about.
          if (!cart || cart.item_count === 0) return;
          var total = cartGross(cart);
          var freeShipMin = 240;
          var ladder = ((state.config && state.config.discount_ladder) || {}).tiers || [];
          ladder = ladder.slice().sort(function (a, b) { return a.threshold - b.threshold; });
          var nxt = null;
          for (var i = 0; i < ladder.length; i++) {
            if (total < ladder[i].threshold) { nxt = ladder[i]; break; }
          }

          var text, cta;
          if (total < freeShipMin) {
            text = "You're ₹" + Math.round(freeShipMin - total) +
                   " from free delivery and Cash on Delivery. Want me to find " +
                   "something small to get you there?";
            cta = "Show me";
          } else if (nxt) {
            text = "Your ₹" + Math.round(total) + " cart already has free delivery and " +
                   "Cash on Delivery, and ₹" + Math.round(nxt.threshold - total) +
                   " more unlocks " + nxt.percent + "% off. If something didn't work, " +
                   "tell me and I'll sort it.";
            cta = "I had a problem";
          } else {
            text = "Your ₹" + Math.round(total) + " cart already has free delivery and " +
                   "Cash on Delivery. If something didn't work at checkout, tell me " +
                   "and I'll sort it.";
            cta = "I had a problem";
          }

          modalWithRetry("checkout_return", {
            title: "Anything stop you at checkout?",
            text: text,
            cta: cta,
          }, function () {
            openPanel();
            send("I tried to check out but didn't complete the order.");
          }, function () {
            try { sessionStorage.setItem("hb_checkout_return_shown", "1"); } catch (e) {}
          }, 6);
        }).catch(function () {});
    }, 4000);
  }

  function scheduleNudges() {
    if (!slotOn("tooltips")) return;
    var nudges = (state.config && state.config.nudges) || [];
    if (!Array.isArray(nudges)) nudges = nudges.rows || nudges.list || [];
    var path = location.pathname;
    var src = adSource();
    var fired = {};
    try { fired = JSON.parse(sessionStorage.getItem("hb_nudged") || "{}"); } catch (e) {}

    var best = null, bestScore = -1;
    nudges.forEach(function (n) {
      if (!n.enabled || !matchPage(n.page, path)) return;
      var utm = (n.utm || "").toLowerCase();
      if (utm) {
        if (!src) return;
        if (utm !== "any" && utm !== src) return;
      }
      var key = n.page + "|" + n.text;
      if (fired[key]) return;
      var score = (utm ? 4 : 0) + (n.page.slice(-1) === "*" ? 1 : 3);
      if (score > bestScore) { bestScore = score; best = n; }
    });
    if (!best) return;

    var text = best.text;
    var pn = productName();
    if (text.indexOf("{product}") !== -1) {
      if (!pn) return;
      if (pn.length > 40) pn = pn.slice(0, 37) + "…";
      text = text.replace(/\{product\}/g, pn);
    }
    var key = best.page + "|" + best.text;
    setTimeout(function () {
      if (state.open) return;
      fired[key] = 1;
      try { sessionStorage.setItem("hb_nudged", JSON.stringify(fired)); } catch (e) {}
      showTip(text);
    }, (best.delay_s || 12) * 1000);
  }

  // ---------- boot ----------
  function detectDevice() {
    try {
      if (navigator.userAgentData && navigator.userAgentData.getHighEntropyValues) {
        return navigator.userAgentData.getHighEntropyValues(["model"])
          .then(function (d) { return d.model || null; })
          .catch(function () { return null; });
      }
    } catch (e) {}
    return Promise.resolve(null);
  }

  fetch(BACKEND + "/widget-config")
    .then(function (r) { return r.json(); })
    .then(function (cfg) {
      state.config = cfg;
      if (cfg.kill_switch && cfg.kill_switch.enabled) {
        bubble.remove(); panel.remove(); throw "disabled";
      }
      return detectDevice();
    })
    .then(function (model) {
      var stored = null;
      try { stored = localStorage.getItem("hb_session"); } catch (e) {}
      return api("/session", { session_key: stored, device_raw: model, path: location.pathname });
    })
    .then(function (s) {
      state.sessionKey = s.session_key;
      state.chatId = s.chat_id || "";
      state.devicePrompt = s.device_prompt || null;
      var resumed = false;
      try {
        resumed = localStorage.getItem("hb_session") === s.session_key;
        localStorage.setItem("hb_session", s.session_key);
      } catch (e) {}
      // each setup is a no-op when its slot is off; wrapped so one failure
      // can never take the others (or the chat) down
      [scheduleNudges, setupIdleModal, setupExitIntent, setupRepeatVisitor,
       setupDwellNudge, setupCartLeaveNudge, setupCartDrawerNudge,
       setupCheckoutReturnNudge, loadPageProduct]
        .forEach(function (fn) { try { fn(); } catch (e) { console.warn("[hatke-bot]", fn.name, e); } });
      // EGRESS: history used to be fetched HERE, on every page view of a
      // resumed session, so the transcript was ready in case the customer
      // opened the chat. Engagement is ~1.3%, so ~99% of those requests were
      // wasted. Just remember that there IS history; firstOpen() fetches it
      // the first time the panel is actually opened.
      state.canResume = resumed;
    })
    .catch(function (e) {
      if (e === "disabled") return;
      console.warn("[hatke-bot] boot failed (backend unreachable or CORS):", e);
      state.bootFailed = true;
      var delays = [3000, 8000, 20000];
      function retryBoot(i) {
        if (i >= delays.length || state.sessionKey) return;
        setTimeout(function () {
          api("/session", { path: location.pathname })
            .then(function (sn) {
              state.sessionKey = sn.session_key;
              state.chatId = sn.chat_id || "";
              state.devicePrompt = sn.device_prompt || null;
              state.bootFailed = false;
              try { localStorage.setItem("hb_session", sn.session_key); } catch (err) {}
              if (state.open && msgs.children.length) {
                msgs.innerHTML = "";
                firstOpen();
              }
            })
            .catch(function () { retryBoot(i + 1); });
        }, delays[i]);
      }
      retryBoot(0);
    });

  } // end __hbMain
})();